import Pusher, { Channel } from "pusher-js";
import { API_URL } from "src/_config";
import { store } from "src/store";

let pusherInstance: Pusher | null = null;

function getPusher(): Pusher {
  if (!pusherInstance) {
    const token = (store.getState() as any).user?.token;
    pusherInstance = new Pusher(process.env.REACT_APP_PUSHER_KEY || "7666e6685c71b188506c", {
      cluster: process.env.REACT_APP_PUSHER_CLUSTER || "ap2",
      channelAuthorization: {
        endpoint: API_URL.CHAT.PUSHER_AUTH,
        transport: "ajax",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    });
  }
  return pusherInstance;
}

/**
 * Subscribe to a private conversation channel.
 * Returns an unsubscribe function to call on cleanup.
 */
export function subscribeToConversation(
  conversationId: string,
  onNewMessage: (msg: any) => void
): () => void {
  const pusher = getPusher();
  const channelName = `private-conversation.${conversationId}`;
  const channel: Channel = pusher.subscribe(channelName);
  channel.bind("new-message", onNewMessage);

  return () => {
    channel.unbind("new-message", onNewMessage);
    pusher.unsubscribe(channelName);
  };
}

/**
 * Subscribe to a private job channel for AI screening and other job-scoped events.
 */
export function subscribeToEmployerJobChannel(
  jobId: string,
  handlers: {
    onAiScreeningComplete?: (data: any) => void;
  }
): () => void {
  const pusher = getPusher();
  const channelName = `private-employer-job.${jobId}`;
  const channel: Channel = pusher.subscribe(channelName);

  const onAiScreeningComplete = (data: any) => {
    handlers.onAiScreeningComplete?.(data);
  };

  channel.bind("ai-screening-complete", onAiScreeningComplete);

  return () => {
    channel.unbind("ai-screening-complete", onAiScreeningComplete);
    pusher.unsubscribe(channelName);
  };
}

/**
 * Subscribe to the employer user's private channel for chat and notification updates.
 * Returns an unsubscribe function (unbinds only; channel stays subscribed for other listeners).
 */
export function subscribeToEmployerChannel(
  userId: number,
  handlers: {
    onUnreadUpdate?: (data: { total_unread: number }) => void;
    onNotificationUpdate?: (data: { unread_count?: number }) => void;
  }
): () => void {
  const pusher = getPusher();
  const channelName = `private-employer-user.${userId}`;
  const channel: Channel = pusher.subscribe(channelName);

  const { onUnreadUpdate, onNotificationUpdate } = handlers;

  if (onUnreadUpdate) {
    channel.bind("unread-count-updated", onUnreadUpdate);
  }
  if (onNotificationUpdate) {
    channel.bind("notifications-updated", onNotificationUpdate);
  }

  return () => {
    if (onUnreadUpdate) {
      channel.unbind("unread-count-updated", onUnreadUpdate);
    }
    if (onNotificationUpdate) {
      channel.unbind("notifications-updated", onNotificationUpdate);
    }
  };
}

/**
 * @deprecated Use subscribeToEmployerChannel with onNotificationUpdate instead.
 */
export function subscribeToNotificationUpdates(
  userId: number,
  onNotificationUpdate: (data: { unread_count?: number }) => void
): () => void {
  return subscribeToEmployerChannel(userId, { onNotificationUpdate });
}

/** Destroy the Pusher instance (call on logout). */
export function disconnectPusher(): void {
  if (pusherInstance) {
    pusherInstance.disconnect();
    pusherInstance = null;
  }
}
